import { useState } from 'react';
import { TrustpilotData } from '../../../mocks/consumer-reviews';

interface Props {
  data: TrustpilotData;
}

function StarRow({ rating, size = 'base' }: { rating: number; size?: 'sm' | 'base' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-xl' : 'text-sm';
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`${sizeClass} ${i < rating ? 'ri-star-fill' : i < rating && rating % 1 >= 0.5 ? 'ri-star-half-fill' : 'ri-star-line'}`}
          style={{ color: i < Math.round(rating) ? '#00B67A' : '#D4D4D4' }}
        ></i>
      ))}
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const bg = score >= 4.5 ? '#00B67A' : score >= 3.5 ? '#73CF11' : score >= 2.5 ? '#FFCE00' : score >= 1.5 ? '#FF8622' : '#FF3722';
  const label = score >= 4.5 ? 'Excellent' : score >= 3.5 ? 'Great' : score >= 2.5 ? 'Average' : score >= 1.5 ? 'Poor' : 'Bad';
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-12 h-12 flex items-center justify-center rounded-xl text-white font-black text-lg flex-shrink-0"
        style={{ background: bg }}
      >
        {score.toFixed(1)}
      </div>
      <div>
        <p className="text-sm font-black text-gray-900">{label}</p>
        <p className="text-xs text-gray-400">Trustpilot Rating</p>
      </div>
    </div>
  );
}

export default function ConsumerReviews({ data }: Props) {
  const [showAll, setShowAll] = useState(false);
  const visibleReviews = showAll ? data.reviews : data.reviews.slice(0, 3);
  const totalPercent100 = data.distribution.reduce((sum, d) => sum + d.percent, 0);

  return (
    <div>
      <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded text-white text-xs font-black"
          style={{ background: '#00B67A' }}
        >
          <i className="ri-star-fill"></i>
        </span>
        User Reviews from Trustpilot
      </h2>

      {/* Summary card */}
      <div className="rounded-2xl border border-gray-100 p-6 mb-6" style={{ background: '#f9fafb' }}>
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Score */}
          <div className="md:w-48 flex-shrink-0 flex flex-col items-center justify-center py-2">
            <div
              className="w-20 h-20 flex items-center justify-center rounded-2xl text-white font-black text-3xl mb-2"
              style={{ background: data.score >= 4.5 ? '#00B67A' : data.score >= 3.5 ? '#73CF11' : data.score >= 2.5 ? '#FFCE00' : '#FF8622' }}
            >
              {data.score.toFixed(1)}
            </div>
            <StarRow rating={data.score} size="base" />
            <p className="text-xs text-gray-400 mt-1">
              {data.score >= 4.5 ? 'Excellent' : data.score >= 3.5 ? 'Great' : data.score >= 2.5 ? 'Average' : 'Poor'}
            </p>
            <p className="text-xs font-semibold text-gray-600 mt-0.5">
              {data.totalReviews.toLocaleString()} reviews
            </p>
          </div>

          {/* Distribution */}
          <div className="flex-1 space-y-2.5">
            {data.distribution.map((d) => (
              <div key={d.star} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-14 flex-shrink-0">
                  <span className="text-xs font-semibold text-gray-600">{d.star}</span>
                  <i className="ri-star-fill text-xs" style={{ color: '#00B67A' }}></i>
                </div>
                <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${(d.percent / totalPercent100) * 100}%`,
                      background: d.star >= 4 ? '#00B67A' : d.star === 3 ? '#FFCE00' : '#FF8622',
                    }}
                  ></div>
                </div>
                <span className="text-xs text-gray-400 w-10 text-right flex-shrink-0">{d.percent}%</span>
              </div>
            ))}
          </div>

          {/* Trustpilot badge */}
          <div className="md:w-40 flex-shrink-0 flex flex-col items-center gap-2 border-l border-gray-200 pl-6">
            <div className="flex items-center gap-1.5">
              <span className="inline-flex w-6 h-6 items-center justify-center rounded" style={{ background: '#00B67A' }}>
                <i className="ri-star-fill text-white text-xs"></i>
              </span>
              <span className="text-sm font-black text-gray-900">Trustpilot</span>
            </div>
            <p className="text-xs text-gray-400 text-center leading-relaxed">
              Based on verified consumer reviews
            </p>
            <a
              href={`https://www.trustpilot.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold underline cursor-pointer"
              style={{ color: '#00B67A' }}
            >
              View on Trustpilot
            </a>
          </div>
        </div>
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {visibleReviews.map((review, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div
                className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-black text-white"
                style={{
                  background: `hsl(${(review.name.charCodeAt(0) * 37) % 360}, 55%, 52%)`,
                }}
              >
                {review.name.charAt(0)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-gray-900">{review.name}</span>
                  {review.verified && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                      <i className="ri-shield-check-fill text-xs"></i> Verified
                    </span>
                  )}
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <i className="ri-map-pin-line text-xs"></i> {review.location}
                  </span>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400">{review.date}</span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <StarRow rating={review.rating} size="sm" />
                </div>

                <p className="text-sm font-bold text-gray-900 mb-1">{review.title}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{review.content}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more / less */}
      {data.reviews.length > 3 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-800 transition-all cursor-pointer bg-white"
        >
          {showAll ? (
            <><i className="ri-arrow-up-s-line mr-1"></i> Show less</>
          ) : (
            <><i className="ri-arrow-down-s-line mr-1"></i> Show all {data.reviews.length} reviews</>
          )}
        </button>
      )}
    </div>
  );
}
